import Input from '../common/input';
import { Translate, useTranslate } from '~/i18n';
import { useForm } from 'react-hook-form';
import Image from 'next/image';
import Axios from '~/utils/axios';
import { useState } from 'react';
import validator from 'validator';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import Loading from '../common/loaders/loader';
import Button from '../common/button';
import { publicUrl } from '~/utils/publicUrl';
import toast from 'react-hot-toast';
import { Sucess } from '~/svgs/business';
import {
   businessBottomRequestBtnEvent,
   businessRequestCallSuccessEvent,
   businessRequestCallFailureEvent,
} from '~/utils/mixpanel-events/business';
import WhatsappContactButton from '../common/whatsapp-contact';
import { sentryCaptureException } from '~/errors/sentry';

type SubmitObject = {
   name: string;
   phone_number: string;
   company_name: string;
   email: string;
};

export const ContactForm = () => {
   const { translate, lang } = useTranslate();
   const { register, handleSubmit, reset, getValues, errors, setError } = useForm<SubmitObject>({
      mode: 'onChange',
   });

   const [loading, setLoading] = useState(false);
   const values = getValues();
   const [success, setSuccess] = useState(false);
   const [captcha, setCaptcha] = useState('');

   const submitHandler = async (obj: SubmitObject) => {
      if (loading) return;
      setLoading(true);
      const trackObj = {
         name: obj?.name,
         email: obj?.email,
         mobile: obj?.phone_number,
         business_name: obj?.company_name,
      };
      businessBottomRequestBtnEvent(trackObj);
      try {
         const locale = lang?.split('-')[1];
         const { data } = await Axios.post('/business-email', {
            ...obj,
            captcha,
            lang: locale,
         });
         if (data.status === 'Success') {
            businessRequestCallSuccessEvent(trackObj);
            setLoading(false);
            setSuccess(true);
            reset();

            toast.success(translate({ id: 'business:success toaster' }));
         } else {
            if (data.error) {
               Object.entries(data.error).map(([key, value], idx) => {
                  setError(key as any, { message: value + '', shouldFocus: idx === 0 });
               });
            }
            throw new Error(translate({ id: 'business:failed toaster' }));
         }
      } catch (error: any) {
         businessRequestCallFailureEvent(trackObj);
         toast.error(error.message);
         sentryCaptureException(error);
      } finally {
         setLoading(false);
      }
   };

   return (
      <div className="grid lg:grid-cols-2 lg:mx-2 bg-[#1D4ED8] rounded-3xl lg:py-16 lg:my-24 my-10">
         <div className="px-8 py-8 pt-12 lg:px-20  border-r-gray-light border-r border-opacity-20 lg:py-8">
            <div className="text-white font-semibold text-4xl  tracking-[-2.53px] pt-8 text-center">
               <Translate id="tickets:join-today" />
            </div>

            <div className="mt-[100px]">
               <Image
                  src={publicUrl('images/business/sports-esports-tournaments.png')}
                  alt="halayalla"
                  width={473.28}
                  height={311}
               />
            </div>
         </div>

         <div className="px-4 py-8 lg:px-20 lg:py-8">
            {success ? (
               <div className="text-white grid border-t-2 border-gray-100 justify-items-center lg:border-0">
                  <div className="lg:text-[45px] lg:pb-[100px] pb-10  pt-8 lg:pt-4 text-[28px]">
                     <Translate id="business:thankyou" />{' '}
                  </div>
                  <Sucess />
                  <div className="lg:pt-[80px] pt-10 lg:text-[24px] text-white text-center text-[16px]">
                     <Translate id="business:team" />
                  </div>
               </div>
            ) : (
               <div className="bg-white border-t-2 border-gray-100 lg:border-0 py-8 px-5 shadow-lg  rounded-3xl">
                  <div className="text-4xl font-semibold pt-4 lg:px-4 text-[#1D4ED8] !text-center lg:text-justify mb-[25px]">
                     <Translate id={`tickets:let’s help you grow your business`} />
                  </div>

                  <form onSubmit={handleSubmit(submitHandler)} id="request-callback-form">
                     <Input
                        type="text"
                        placeholder={translate({ id: 'business:enter name' })}
                        className="rounded-lg bg-gray-light !px-4"
                        groupClassName="mb-6"
                        id="name"
                        name="name"
                        error={errors.name?.message}
                        valid={!!values.name}
                        innerRef={register({
                           required: translate({ id: 'business:required field' }),
                        })}
                     />
                     <Input
                        type="text"
                        placeholder={translate({ id: 'business:enter mobile number' })}
                        className="rounded-lg bg-gray-light !px-4"
                        groupClassName="mb-6"
                        id="phone_number"
                        name="phone_number"
                        inputMode="numeric"
                        error={
                           errors.phone_number?.message && <Translate id="business:validNumber" />
                        }
                        valid={!!values.phone_number}
                        innerRef={register({
                           required: translate({ id: 'business:required field' }),
                        })}
                     />
                     <Input
                        type="text"
                        placeholder={translate({ id: 'business:enter business name' })}
                        className="rounded-lg bg-gray-light !px-4"
                        groupClassName="mb-6"
                        id="company name"
                        name="company_name"
                        error={errors.company_name?.message}
                        valid={!!values.company_name}
                        innerRef={register({
                           required: translate({ id: 'business:required field' }),
                        })}
                     />
                     <Input
                        type="email"
                        id="email"
                        name="email"
                        placeholder={translate({ id: 'business:enter email' })}
                        className="rounded-lg bg-gray-light !px-4"
                        groupClassName="mb-6"
                        innerRef={register({
                           validate: value =>
                              validator.isEmail(value) || translate({ id: 'auth:invalid email' }),
                           required: translate({ id: 'business:required field' }),
                           pattern: {
                              value: /^((?!hotmail|outlook|gmail|yahoo|live|icloud).)*$/,
                              message: translate({ id: 'auth:email domain mismatch' }),
                           },
                        })}
                        error={errors.email?.message}
                        valid={!!values.email}
                     />
                     <GoogleReCaptcha onVerify={setCaptcha} />
                     <div className="flex justify-center">
                        <Button
                           btnType="submit"
                           className="lg:w-[410px] w-[240px] bg-[#1D4ED8] requestcallback-bottom border-transparent"
                           disabled={loading}>
                           <div className="!text-white">
                              {loading ? (
                                 <Loading color="white" />
                              ) : (
                                 <>
                                    <Translate id={`tickets:get in touch`} />
                                 </>
                              )}
                           </div>
                        </Button>
                     </div>
                  </form>
                  <div className="text-[#6E757C] text-center max-w-[280px] mx-auto mt-4 mb-8">
                     <Translate id="contact:We will reach out to you" />
                  </div>
                  <hr className="mb-8" />
                  <WhatsappContactButton />
               </div>
            )}
         </div>
      </div>
   );
};
